import { Alert, ListGroup } from "react-bootstrap";
import Spinner from "../../../components/Spinner";
import SubmitButton from "../../../components/SubmitButton";
import { HouseholdPerson, stepErrorMessage } from "./api";

/**
 * Step 1 — Confirm the people to switch.
 *
 * Lists the household's people (read from AdviceCloud, the canonical store) and
 * asks the advisor to confirm them before searching Slant. Nothing is written
 * in this step; confirming only advances the wizard to Step 2. The confirmed
 * people are held in the wizard's React state, not on the server.
 */
const SwitchToSlantStep1 = ({
  people,
  isLoading,
  isError,
  error,
  onConfirm,
}: {
  people: HouseholdPerson[];
  isLoading: boolean;
  isError: boolean;
  /** The failed `useHouseholdPeople` error, read only when `isError`. */
  error: unknown;
  onConfirm: () => void;
}) => {
  if (isLoading) {
    return (
      <div className="d-flex align-items-center">
        <Spinner as="span" size="sm" className="me-2" />
        <span>Loading the household's people…</span>
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="danger">
        {stepErrorMessage(error, "Failed to load the household's people.")}
      </Alert>
    );
  }

  return (
    <div>
      <h5>Confirm the people to switch</h5>
      <p className="text-muted">
        These are the people on this household. Confirm them before searching
        Slant — nothing is written to Slant in this step.
      </p>

      {people.length === 0 ? (
        <Alert variant="warning">
          This household has no people to switch. Add the household&apos;s
          members in AdviceCloud first, then start the switch again.
        </Alert>
      ) : (
        <ListGroup className="mb-3">
          {people.map((person) => (
            <ListGroup.Item key={person.contact_id}>
              {person.name}
              {person.email ? (
                <small className="text-muted"> · {person.email}</small>
              ) : null}
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}

      <Alert variant="info">
        Notes and tasks held in the CRM are migrated separately by the upstream
        bulk migration — they are not selected here.
      </Alert>

      <div className="d-flex justify-content-end">
        <SubmitButton
          label="Confirm — find in Slant"
          onClick={onConfirm}
          isSubmitting={false}
          disabled={people.length === 0}
        />
      </div>
    </div>
  );
};

export default SwitchToSlantStep1;
